/**
 * Feature Toggle Service
 *
 * Reads platform_feature_toggles from Supabase.
 * Toggles are public (anon + authenticated can SELECT).
 * Pattern follows logger_groups_config and ai_function_config.
 */

import { supabase } from '@deskbay/shared/services/supabase/client';
import { logger } from '@deskbay/shared';

export interface FeatureToggle {
    id: string;
    feature_key: string;
    display_name: string;
    description: string;
    category: 'authentication' | 'payments' | 'ui' | 'general' | 'tickets';
    is_active: boolean;
    sort_order: number;
    updated_at: string;
    updated_by: string | null;
    created_at: string;
}

/**
 * Fetch all feature toggles from DB.
 * Returns empty array on error (non-blocking).
 */
export async function fetchFeatureToggles(): Promise<FeatureToggle[]> {
    try {
        const { data, error } = await supabase
            .from('platform_feature_toggles')
            .select('*')
            .order('sort_order', { ascending: true });

        if (error) {
            logger.api.error('Failed to fetch feature toggles', error, 'featureToggleService');
            return [];
        }

        return (data ?? []) as FeatureToggle[];
    } catch (error: unknown) {
        logger.api.error('Feature toggles fetch exception', error, 'featureToggleService');
        return [];
    }
}

/**
 * Check if a specific feature is active.
 * Helper for quick lookups from a toggles array.
 */
export function isFeatureActive(toggles: FeatureToggle[], featureKey: string): boolean {
    const toggle = toggles.find(t => t.feature_key === featureKey);
    // Default to true if toggle not found (safe fallback — don't hide features by accident)
    return toggle?.is_active ?? true;
}
